import { presenceRowIsXpro4 } from "./teamPresenceUsersSnapshot";

/**
 * Normaliza o payload do evento socket `users` (formatos diferentes no mesmo servidor).
 * - `{ queue_id, data: [ ... ] }` (lista)
 * - `{ queue_id, data: { id, ... } }` (um objeto)
 * - `{ id, name, workspace, client?, ... }` (objeto único por emissão / eco)
 */
export function normalizeUsersSocketUsersPayload(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data != null && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    const id = Number(payload.data.id);
    if (Number.isFinite(id)) return [payload.data];
  }
  if (typeof payload === "object" && Number.isFinite(Number(payload.id))) {
    if (payload.queue_id != null && payload.data == null) return [];
    return [payload];
  }
  return [];
}

/**
 * Mescla linhas “mínimas” (id, name, workspace, socketId) com a última linha rica do mesmo socket/tab.
 * @param {object[]} rows
 * @param {Map<string, object>} cache — chaves `sid:…`, `uidtab:…`
 */
export function enrichPresenceRowsFromSocketCache(rows, cache) {
  return rows.map((row) => {
    if (presenceRowIsXpro4(row)) return row;
    const sid = row?.socketId != null ? String(row.socketId).trim() : "";
    if (sid && cache.has(`sid:${sid}`)) {
      const rich = cache.get(`sid:${sid}`);
      if (rich && presenceRowIsXpro4(rich)) {
        return mergeRichIntoRow(row, rich);
      }
    }
    const uid = Number(row?.id);
    const tab = row?.tab;
    if (Number.isFinite(uid) && tab != null && cache.has(`uidtab:${uid}:${String(tab)}`)) {
      const rich = cache.get(`uidtab:${uid}:${String(tab)}`);
      if (rich && presenceRowIsXpro4(rich)) {
        return mergeRichIntoRow(row, rich);
      }
    }
    return row;
  });
}

function mergeRichIntoRow(row, rich) {
  return {
    ...row,
    client: rich.client ?? row.client,
    app: rich.app ?? row.app,
    tab: rich.tab ?? row.tab,
    path: rich.path ?? row.path,
    since: rich.since ?? row.since,
    lastActivityAt: rich.lastActivityAt ?? row.lastActivityAt,
    focused: rich.focused !== undefined ? rich.focused : row.focused,
  };
}

/**
 * Guarda linhas Xpro 4 completas para cruzar com snapshots enxutos posteriores.
 * @param {object[]} rows
 * @param {Map<string, object>} cache
 */
export function rememberRichPresenceRowsInCache(rows, cache, { maxKeys = 900 } = {}) {
  for (const row of rows) {
    if (!presenceRowIsXpro4(row)) continue;
    const sid = row?.socketId != null ? String(row.socketId).trim() : "";
    if (sid) cache.set(`sid:${sid}`, { ...row });
    const uid = Number(row?.id);
    if (Number.isFinite(uid) && row.tab != null) {
      cache.set(`uidtab:${uid}:${String(row.tab)}`, { ...row });
    }
  }
  while (cache.size > maxKeys) {
    const first = cache.keys().next().value;
    if (first == null) break;
    cache.delete(first);
  }
}

/**
 * Normaliza, atualiza cache com linhas ricas do próprio pacote, enriquece mínimas e atualiza o cache de novo.
 * @param {object} payload
 * @param {Map<string, object>} cache
 */
export function prepareUsersSocketRowsForPresence(payload, cache) {
  const rows = normalizeUsersSocketUsersPayload(payload);
  rememberRichPresenceRowsInCache(rows, cache);
  const enriched = enrichPresenceRowsFromSocketCache(rows, cache);
  rememberRichPresenceRowsInCache(enriched, cache);
  return enriched;
}

/** Cache compartilhado: várias telas montam `useTeamSocketPresence` e o cache não pode morrer na desmontagem. */
const mainAppPresenceSocketMergeCache = new Map();

export function mergePresenceSocketRowsForMainApp(payload) {
  return prepareUsersSocketRowsForPresence(payload, mainAppPresenceSocketMergeCache);
}

export function clearMainAppPresenceSocketMergeCache() {
  mainAppPresenceSocketMergeCache.clear();
}
